/**
 * WindowSelector — global insights display-window control for `/neat`.
 *
 * Preset chips (Trailing 12 mo default · 6 mo · 3 mo · YTD) plus a Custom
 * toggle revealing two month inputs. Selection is persisted in the URL via
 * `useInsightsWindow` and threaded into every insight fetch. Observed-window
 * framing only — this scopes what data is shown, not any savings claim.
 */
import type React from "react"
import { useState } from "react"

import { Box, Button, Stack, TextField, Typography } from "@mui/material"

import { useInsightsWindow } from "../data/use-insights-window"

const PRESETS: { key: string; label: string }[] = [
  { key: "trailing_12mo", label: "Trailing 12 mo" },
  { key: "trailing_6mo", label: "Trailing 6 mo" },
  { key: "trailing_3mo", label: "Trailing 3 mo" },
  { key: "ytd", label: "YTD" },
]

export const WindowSelector: React.FC = () => {
  const { window, start, end, label, setPreset, setCustom } =
    useInsightsWindow()
  const isCustom = start != null && end != null
  const [customOpen, setCustomOpen] = useState(isCustom)
  // The two months are held here, not read back from the URL, because the URL
  // only gains them once BOTH are chosen. Reading them from the URL made each
  // field wait for the other, so from a clean state neither could ever be the
  // first one set and a custom range could never be applied.
  const [from, setFrom] = useState(start ?? "")
  const [to, setTo] = useState(end ?? "")
  const activePreset = isCustom ? null : (window ?? "trailing_12mo")

  // Both the BFF enricher and insights-api drop a start > end range and fall
  // back to the preset, so applying one would leave the control showing a
  // selection the numbers underneath do not honour. Hold it back and say why.
  const inverted = from !== "" && to !== "" && from > to
  const applyRange = (nextFrom: string, nextTo: string): void => {
    if (nextFrom === "" || nextTo === "" || nextFrom > nextTo) {
      return
    }
    setCustom(nextFrom, nextTo)
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
    >
      <Typography color="text.secondary" variant="body2">
        Window:
      </Typography>
      {PRESETS.map((p) => (
        <Button
          key={p.key}
          onClick={() => {
            setCustomOpen(false)
            setPreset(p.key)
          }}
          size="small"
          variant={activePreset === p.key ? "contained" : "outlined"}
        >
          {p.label}
        </Button>
      ))}
      <Button
        onClick={() => {
          setCustomOpen((v) => !v)
        }}
        size="small"
        variant={isCustom ? "contained" : "outlined"}
      >
        Custom
      </Button>
      {customOpen ? (
        <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
          <TextField
            label="From"
            error={inverted}
            onChange={(e) => {
              setFrom(e.target.value)
              applyRange(e.target.value, to)
            }}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            type="month"
            value={from}
          />
          <TextField
            label="To"
            error={inverted}
            helperText={inverted ? "To must not be before From" : undefined}
            onChange={(e) => {
              setTo(e.target.value)
              applyRange(from, e.target.value)
            }}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            type="month"
            value={to}
          />
        </Box>
      ) : null}
      {isCustom ? (
        <Typography color="text.secondary" sx={{ ml: 1 }} variant="body2">
          {label}
        </Typography>
      ) : null}
    </Stack>
  )
}
